import { Action } from 'redux'
import { isType } from 'ts-action'
import {
  IMyUpshifter,
  IMyUpshiftersReducer,
} from '@network/models/my-upshifter.model'
import {
  DeleteFavorite,
  UnfavoriteUpshifter,
  AddFavorite as FavoriteUpshifter,
} from 'data/business/Favorite/actions'
import { deleteFromArray } from 'helpers/helperFuncs'
import {
  AddToMultipleRosters,
  DeleteFromRoster,
  GetMyUpshifters,
  MyUpshiftersFetching,
  ResetMyUpshiftersSorting,
  SetDeleteFromRosterLoading,
  SetMyUpshiftersSorting,
} from './actions'

const initialState: IMyUpshiftersReducer = {
  meta: {
    code: 0,
    message: '',
  },
  isFetching: false,
  data: [],
  deleteFromRosterLoadingIds: [],
  sortingState: {
    columnId: null,
    direction: null,
  },
}

export const myUpshiftersReducer = (
  state = initialState,
  action: Action,
): IMyUpshiftersReducer => {
  if (isType(action, GetMyUpshifters)) {
    const { payload } = action
    return {
      ...state,
      meta: payload.meta,
      data: payload.data,
    }
  }
  if (isType(action, MyUpshiftersFetching)) {
    const { payload } = action
    return {
      ...state,
      isFetching: payload,
    }
  }
  if (isType(action, UnfavoriteUpshifter)) {
    const { payload } = action
    const { data, meta } = state
    const formattedArray = deleteFromArray<IMyUpshifter>(data, payload)
    return {
      ...state,
      data: formattedArray,
      meta: {
        ...meta,
        pagination: {
          ...meta.pagination,
          count: meta.pagination ? meta.pagination.count - 1 : 0,
          total: meta.pagination ? meta.pagination.total - 1 : 0,
          per_page: meta.pagination?.per_page || 50,
          current_page: meta.pagination?.current_page || 1,
          total_pages: meta.pagination?.total_pages || 1,
          links: meta.pagination?.links || [],
        },
      },
    }
  }
  if (isType(action, DeleteFavorite)) {
    const { payload } = action
    const { data } = state
    const formattedArray = data.map(item => {
      if (item.id !== payload) {
        return item
      }
      return { ...item, favored_by_business: null }
    })

    return {
      ...state,
      data: formattedArray,
    }
  }
  if (isType(action, FavoriteUpshifter)) {
    const {
      payload: { data },
    } = action
    const updatedUpshifters = state.data.map(upshifter => {
      if (upshifter.id === data.model_favored.id) {
        return {
          ...upshifter,
          favored_by_business: data,
        }
      }
      return upshifter
    })

    // If the Business user Favorites an Upshifter via All/Blocked tabs, then the Upshifter remains in the UpshiftersList list and here we just return the same list but with changed 'favorite' property.
    if (state.data.find(item => item.id === data.model_favored.id)) {
      return {
        ...state,
        data: updatedUpshifters,
      }
    }
    // Otherwise, if the Business user Favorites an Upshifter via Favorites (My Team) tab, then the Upshifter needs to be added in the Upshifters List, since the list displayed in My Team tab is changing its list items depending on the Favorite/Unfavorite actions.
    return {
      ...state,
      data: [
        ...updatedUpshifters,
        (data.model_favored as unknown) as IMyUpshifter,
      ],
    }
  }

  if (isType(action, SetDeleteFromRosterLoading)) {
    const { payload } = action
    return {
      ...state,
      deleteFromRosterLoadingIds: {
        ...state.deleteFromRosterLoadingIds,
        [payload.id]: payload.value,
      },
    }
  }

  if (isType(action, DeleteFromRoster)) {
    const { payload } = action
    const { data } = state
    const formattedArray = deleteFromArray<IMyUpshifter>(data, payload)
    return {
      ...state,
      data: formattedArray,
      meta: {
        ...state.meta,
        pagination: {
          count: state.meta.pagination?.count
            ? state.meta.pagination.count - 1
            : 0,
          total: state.meta.pagination?.total
            ? state.meta.pagination.total - 1
            : 0,
          per_page: state.meta.pagination?.per_page ?? 50,
          current_page: state.meta.pagination?.current_page ?? 1,
          total_pages: state.meta.pagination?.total_pages ?? 1,
          links: state.meta.pagination?.links ?? [],
        },
      },
    }
  }

  if (isType(action, AddToMultipleRosters)) {
    const {
      payload: { upshifterId, rosters },
    } = action
    const { data } = state

    const updatedUpshifters = data.map(upshifter => {
      if (upshifter.id === upshifterId) {
        return {
          ...upshifter,
          rosters: {
            ...upshifter.rosters,
            data: [...(upshifter.rosters?.data || []), ...rosters],
          },
        }
      }
      return upshifter
    })

    return {
      ...state,
      data: updatedUpshifters,
    }
  }

  if (isType(action, SetMyUpshiftersSorting)) {
    const { payload } = action
    return {
      ...state,
      sortingState: payload,
    }
  }
  if (isType(action, ResetMyUpshiftersSorting)) {
    return {
      ...state,
      sortingState: {
        columnId: null,
        direction: null,
      },
    }
  }

  return state
}
